import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, Home, Link2, RefreshCw, Unlink, CheckCircle2, AlertTriangle, Search,
  ChevronRight, ExternalLink, Zap,
} from 'lucide-react';
import { toast } from 'sonner';

import API_URL from '@/config/api';
import AuthContext from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { formatApiError } from '@/utils/apiError';
import { XeroBusinessMap } from '@/components/xero/XeroBusinessMap';

/**
 * /staff/xero — Xero integration control panel.
 * Sections:
 *   1) Connection status card (Connect / Sync now / Disconnect)
 *   2) Imported Xero items list (search + filter)
 *   3) Portal-to-Xero matching (link a portal stock_product to a Xero item)
 */
export default function XeroIntegration() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [searchParams, setSearchParams] = useSearchParams();

  const [status, setStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [items, setItems] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [q, setQ] = useState('');

  const isAdmin = user && ['admin', 'manager'].includes(user.role);

  // Show connect/error toast on redirect back from Xero.
  useEffect(() => {
    const xeroParam = searchParams.get('xero');
    if (xeroParam === 'connected') {
      toast.success('Xero connected — initial product import in progress.');
      // Strip the query param so a refresh doesn't retoast.
      searchParams.delete('xero');
      setSearchParams(searchParams, { replace: true });
    } else if (xeroParam === 'error') {
      const reason = searchParams.get('reason') || 'unknown';
      toast.error(`Xero connect failed: ${reason.replace(/_/g, ' ')}`);
      searchParams.delete('xero');
      searchParams.delete('reason');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const loadStatus = useCallback(async () => {
    setStatusLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/xero/status`, { credentials: 'include' });
      if (!res.ok) throw new Error(formatApiError(await res.json().catch(() => ({}))) || 'Failed to load Xero status');
      setStatus(await res.json());
    } catch (e) { toast.error(e.message); }
    finally { setStatusLoading(false); }
  }, []);

  const loadItems = useCallback(async () => {
    if (!status?.connections?.length) return;
    setItemsLoading(true);
    try {
      const url = new URL(`${API_URL}/api/xero/items`);
      if (q) url.searchParams.set('q', q);
      const res = await fetch(url.toString(), { credentials: 'include' });
      if (!res.ok) throw new Error(formatApiError(await res.json().catch(() => ({}))) || 'Failed to load Xero items');
      const d = await res.json();
      setItems(d.items || []);
    } catch (e) { toast.error(e.message); }
    finally { setItemsLoading(false); }
  }, [q, status]);

  useEffect(() => { loadStatus(); }, [loadStatus]);
  useEffect(() => { loadItems(); }, [loadItems]);

  const connect = () => {
    // Simple redirect — Xero handles the whole authorization dance.
    window.location.href = `${API_URL}/api/xero/connect`;
  };

  const syncNow = async (tenantId) => {
    setSyncing(true);
    try {
      const res = await fetch(`${API_URL}/api/xero/sync/${tenantId}`, {
        method: 'POST', credentials: 'include',
      });
      if (!res.ok) throw new Error(formatApiError(await res.json().catch(() => ({}))) || 'Sync failed');
      const d = await res.json();
      toast.success(`Xero sync complete: ${d.stats?.updated || 0} items updated${d.stats?.errors ? ` (${d.stats.errors} errors)` : ''}`);
      await loadStatus();
      await loadItems();
    } catch (e) { toast.error(e.message); }
    finally { setSyncing(false); }
  };

  const disconnect = async (tenantId) => {
    if (!window.confirm('Disconnect Xero? Product sync will stop until you reconnect.')) return;
    try {
      const res = await fetch(`${API_URL}/api/xero/connections/${tenantId}`, {
        method: 'DELETE', credentials: 'include',
      });
      if (!res.ok) throw new Error(formatApiError(await res.json().catch(() => ({}))) || 'Disconnect failed');
      toast.success('Xero disconnected.');
      await loadStatus();
      setItems([]);
    } catch (e) { toast.error(e.message); }
  };

  const conns = status?.connections || [];
  const connected = conns.length > 0;

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-8">
        <Card className="p-8 text-center max-w-md">
          <AlertTriangle className="w-8 h-8 text-amber-500 mx-auto mb-3" />
          <p className="font-semibold">Admin only</p>
          <p className="text-sm text-gray-600 mt-1">Only admins and managers can view the Xero integration page.</p>
          <Button className="mt-4" variant="outline" onClick={() => navigate(-1)}>Go back</Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="border-b sticky top-0 z-30 shadow-sm bg-slate-900 text-white">
        <div className="max-w-5xl mx-auto px-3 sm:px-4 py-3 flex items-center gap-2 sm:gap-3">
          <button onClick={() => navigate(-1)} className="text-white/80 hover:text-white shrink-0" data-testid="xero-back" aria-label="Back">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <Link to="/" className="text-white/80 hover:text-white shrink-0 hidden sm:inline-flex" aria-label="Home">
            <Home className="w-5 h-5" />
          </Link>
          <div className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center shrink-0">
            <Zap className="w-4 h-4" />
          </div>
          <h1 className="text-base sm:text-lg font-bold truncate flex-1 min-w-0">Xero integration</h1>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-3 sm:px-4 py-6 space-y-4">

        {/* Connection card */}
        <Card className="p-5" data-testid="xero-status-card">
          {statusLoading ? (
            <div className="text-gray-500 italic text-center py-4">Loading Xero status…</div>
          ) : !status?.configured ? (
            <div className="text-amber-800 bg-amber-50 border border-amber-200 rounded p-4 text-sm">
              Xero credentials aren&apos;t configured on the server yet. Ask a developer to set <code>XERO_CLIENT_ID</code>, <code>XERO_CLIENT_SECRET</code>, and <code>XERO_REDIRECT_URI</code> in the backend environment.
            </div>
          ) : !connected ? (
            <div className="text-center">
              <div className="mx-auto w-12 h-12 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center mb-3">
                <Link2 className="w-6 h-6" />
              </div>
              <h2 className="text-lg font-bold">Connect Ox Resin to your Xero account</h2>
              <p className="text-sm text-gray-600 mt-1 max-w-md mx-auto">
                Once connected, we&apos;ll pull every Xero product into the portal and keep prices in sync every 5 minutes.
                Products created here will also push back to Xero automatically.
              </p>
              <Button onClick={connect} className="mt-5 bg-[#13B5EA] hover:bg-[#0f9bc9] text-white font-bold px-6" data-testid="xero-connect-btn">
                <Link2 className="w-4 h-4 mr-2" />
                Connect to Xero
              </Button>
              <p className="text-[11px] text-gray-400 mt-3">You&apos;ll be redirected to Xero&apos;s login page.</p>
              <XeroDebugPanel />
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-dashed border-[#13B5EA]/50 bg-sky-50/50 px-4 py-3" data-testid="xero-connect-another">
                <div className="text-sm">
                  <div className="font-bold text-gray-900">{conns.length} Xero organisation{conns.length === 1 ? '' : 's'} connected</div>
                  <div className="text-xs text-gray-600">Ox Resin runs three — connect each one, then assign it to its business below.</div>
                </div>
                <Button onClick={connect} size="sm" className="bg-[#13B5EA] hover:bg-[#0f9bc9] text-white font-bold" data-testid="xero-connect-another-btn">
                  <Link2 className="w-4 h-4 mr-2" /> Connect another Xero organisation
                </Button>
              </div>
              {/* Diagnostic — surfaced even when connected so we can check
                  scopes and redirect_uri after a scope-config change. */}
              <XeroDebugPanel />
              {conns.map(c => (
                <div key={c.tenant_id} className="border rounded-lg p-4 bg-emerald-50/40" data-testid={`xero-connection-${c.tenant_id}`}>
                  <div className="flex items-start gap-3 flex-wrap">
                    <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0">
                      <CheckCircle2 className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-gray-900">{c.tenant_name || c.tenant_id}</div>
                      <div className="text-[11px] text-gray-500 uppercase tracking-wider">{c.tenant_type || 'ORGANISATION'}</div>
                      <div className="mt-2 text-xs text-gray-700 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
                        <div>Connected by: <span className="font-mono">{c.connected_by_email || '—'}</span></div>
                        <div>Last sync: <span className="font-mono">{c.last_items_sync ? new Date(c.last_items_sync).toLocaleString() : 'never'}</span></div>
                        <div>Tax config: <span className="font-mono">{c.tax_config_synced_at ? new Date(c.tax_config_synced_at).toLocaleString() : 'not yet'}</span></div>
                        <div>Status: <span className={'font-semibold ' + (c.status === 'connected' ? 'text-emerald-700' : 'text-amber-700')}>{c.status}</span></div>
                      </div>
                      {c.last_sync_error && (
                        <div className="mt-2 text-xs bg-red-50 border border-red-200 text-red-800 rounded px-2 py-1">
                          <strong>Sync error:</strong> {c.last_sync_error}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <Button
                        size="sm" variant="outline"
                        onClick={() => syncNow(c.tenant_id)}
                        disabled={syncing}
                        data-testid={`xero-sync-btn-${c.tenant_id}`}
                      >
                        <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${syncing ? 'animate-spin' : ''}`} />
                        Sync now
                      </Button>
                      <Button
                        size="sm" variant="outline"
                        onClick={() => disconnect(c.tenant_id)}
                        className="text-red-700 border-red-200 hover:bg-red-50"
                        data-testid={`xero-disconnect-btn-${c.tenant_id}`}
                      >
                        <Unlink className="w-3.5 h-3.5 mr-1.5" />
                        Disconnect
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {connected && <XeroBusinessMap canEdit={user?.role === 'admin'} refreshKey={conns.length} />}

        {connected && (
          <Card className="overflow-hidden" data-testid="xero-items-card">
            <div className="px-4 py-3 border-b bg-slate-50 flex items-center gap-3 flex-wrap">
              <div>
                <h3 className="font-bold text-gray-900">Xero products</h3>
                <p className="text-xs text-gray-600">Mirror of every Item in your Xero org — matched to portal SKUs where linked.</p>
              </div>
              <div className="ml-auto relative">
                <Search className="w-4 h-4 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                <Input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search code, name, description…"
                  className="pl-8 h-9 w-64 bg-white"
                  data-testid="xero-items-search"
                />
              </div>
            </div>
            {itemsLoading ? (
              <div className="p-8 text-center text-gray-500 italic text-sm">Loading items…</div>
            ) : items.length === 0 ? (
              <div className="p-8 text-center text-gray-500 italic text-sm">
                No items yet. Hit <strong>Sync now</strong> to pull them from Xero.
              </div>
            ) : (
              <div className="max-h-[520px] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-white sticky top-0 border-b text-[10px] uppercase tracking-wider text-gray-500">
                    <tr>
                      <th className="text-left px-3 py-2 font-bold">Code</th>
                      <th className="text-left px-3 py-2 font-bold">Name</th>
                      <th className="text-right px-3 py-2 font-bold">Sale £</th>
                      <th className="text-left px-3 py-2 font-bold">VAT</th>
                      <th className="text-left px-3 py-2 font-bold">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {items.map(it => (
                      <tr key={it.xero_item_id} className="hover:bg-slate-50" data-testid={`xero-item-row-${it.xero_item_id}`}>
                        <td className="px-3 py-2 font-mono text-xs">{it.code || <span className="text-gray-400">—</span>}</td>
                        <td className="px-3 py-2">
                          <div className="font-semibold text-gray-900">{it.name || '(unnamed)'}</div>
                          {it.description && <div className="text-[11px] text-gray-500 line-clamp-1">{it.description}</div>}
                        </td>
                        <td className="px-3 py-2 text-right font-mono">
                          {it.sales_price !== null && it.sales_price !== undefined
                            ? `£${Number(it.sales_price).toFixed(2)}`
                            : <span className="text-gray-400">—</span>}
                        </td>
                        <td className="px-3 py-2 text-xs text-gray-600">{it.sales_tax_type || '—'}</td>
                        <td className="px-3 py-2 text-xs">
                          {it.archived ? (
                            <span className="text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">Archived</span>
                          ) : (
                            <span className="text-emerald-700">Active</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        )}

        {connected && (
          <Card className="p-4 bg-blue-50/60 border-blue-100">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center shrink-0">
                <ChevronRight className="w-4 h-4" />
              </div>
              <div className="text-sm text-blue-900 flex-1">
                <strong>What&apos;s next:</strong>
                <ul className="mt-2 space-y-1 list-disc list-inside text-blue-900/90">
                  <li>Sync runs automatically every 5 minutes — price changes in Xero appear here without any action needed.</li>
                  <li>To link a portal product to a Xero item, open <a href="/staff/stock" className="underline font-semibold">Stock</a> and use the new &ldquo;Link to Xero&rdquo; button on each product row (coming in Phase 1b).</li>
                  <li>Phase 2 (auto-raise draft invoices from portal orders) is queued — will be built once this phase is verified in production.</li>
                </ul>
              </div>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}


/**
 * Collapsible diagnostic — fetches `/api/xero/debug` with the caller's
 * session cookie so admins can see the exact scopes, client_id prefix
 * and redirect_uri the server is about to send Xero. Copy button
 * dumps the JSON to the clipboard so it can be pasted into support
 * threads without a screenshot.
 */
function XeroDebugPanel() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/xero/debug`, { credentials: 'include' });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(formatApiError(body) || `HTTP ${res.status}`);
      setData(body);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const copy = async () => {
    if (!data) return;
    try {
      await navigator.clipboard.writeText(JSON.stringify(data, null, 2));
      toast.success('Diagnostic copied to clipboard.');
    } catch (_) {
      toast.error('Couldn\u2019t copy \u2014 select the text and copy manually.');
    }
  };

  return (
    <div className="mt-6 text-left border border-slate-200 rounded-lg bg-slate-50" data-testid="xero-debug-panel">
      <button
        type="button"
        onClick={() => {
          const nextOpen = !open;
          setOpen(nextOpen);
          if (nextOpen && !data && !loading) load();
        }}
        className="w-full flex items-center justify-between px-4 py-2 text-xs font-bold text-slate-700 hover:bg-slate-100"
        data-testid="xero-debug-toggle"
      >
        <span>{open ? 'Hide diagnostic' : 'Show diagnostic (troubleshoot connect errors)'}</span>
        <ChevronRight className={`w-4 h-4 transition-transform ${open ? 'rotate-90' : ''}`} />
      </button>
      {open && (
        <div className="border-t border-slate-200 px-4 py-3">
          {loading ? (
            <div className="text-xs text-slate-500 italic">Loading diagnostic…</div>
          ) : error ? (
            <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2">
              {error}
            </div>
          ) : data ? (
            <div className="space-y-2">
              <pre className="text-[11px] leading-relaxed bg-white border border-slate-200 rounded p-2 overflow-x-auto whitespace-pre-wrap break-all" data-testid="xero-debug-json">
{JSON.stringify(data, null, 2)}
              </pre>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={copy} data-testid="xero-debug-copy">
                  Copy to clipboard
                </Button>
                <Button size="sm" variant="outline" onClick={load} data-testid="xero-debug-refresh">
                  Refresh
                </Button>
              </div>
              <p className="text-[10px] text-slate-500 italic">
                What to check: <strong>hardcoded_scope_in_code</strong> should list
                {' '}<code>offline_access</code>, <code>accounting.invoices</code>,
                <code>accounting.settings</code> (create &amp; edit products). <strong>redirect_uri_configured</strong>
                {' '}must match the callback URL registered in your Xero developer app exactly (protocol + host + path).
              </p>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
